import React from "react";
import { EyeOff, ScrollText } from "lucide-react";
import { cn } from "../../lib/utils";
import { MapEditorEmptyState, MapEditorPaneHeader } from "./internal/MapEditorKitParts";
import { MapLegendSwatch } from "./internal/MapLegendSwatch";
import type { 
  MapLegendPaneProps, 
  MapLegendSection, 
  MapLegendEntry 
} from "../../contracts/map-workspace";

export function MapLegendPane({
  sections,
  emptyMessage = "No legend data available for this view",
  className,
}: MapLegendPaneProps) {
  if (sections.length === 0) {
    return (
      <MapEditorEmptyState
        className={className}
        icon={<ScrollText className="h-4 w-4" />}
        title="Legend unavailable"
        message={emptyMessage ?? "No legend data available for this view"}
      />
    );
  }

  const renderEntry = (entry: MapLegendEntry) => (
    <div
      key={entry.id}
      className={cn(
        "flex items-center gap-2 py-1 group",
        entry.hidden && "opacity-50 grayscale"
      )}
    >
      <div className="relative shrink-0">
        <MapLegendSwatch
          label={entry.label}
          color={entry.color}
          representation={entry.representation}
          iconToken={entry.iconToken}
          iconPreviewUrl={entry.iconPreviewUrl}
          iconSdf={entry.iconSdf}
        />
        {entry.hidden && (
          <div className="absolute -top-1 -right-1 bg-background rounded-full p-0.5 shadow-sm">
            <EyeOff className="w-2 h-2 text-muted-foreground" />
          </div>
        )}
      </div>
      <span className={cn(
        "text-xs font-medium truncate",
        entry.hidden && "line-through text-muted-foreground"
      )}>
        {entry.label}
      </span>
    </div>
  );

  const renderSection = (section: MapLegendSection) => (
    <div key={section.id} className="flex flex-col gap-2 mb-6 last:mb-0">
      <div className="flex flex-col gap-0.5">
        <h4 className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/80">
          {section.title}
        </h4>
        {section.subtitle && (
          <p className="text-[10px] text-muted-foreground leading-tight italic">
            {section.subtitle}
          </p>
        )}
      </div>
      <div className="flex flex-col px-0.5">
        {section.entries.map(renderEntry)}
      </div>
    </div>
  );

  return (
    <div className={cn("flex flex-col w-full h-full bg-background overflow-hidden", className)}>
      <div className="p-4">
        <MapEditorPaneHeader
          title="Map Legend"
          description="Read the active style at a glance before drilling into a layer."
          icon={<ScrollText className="h-4 w-4" />}
          className="mb-4"
        />
        <div className="flex flex-col">
          {sections.map(renderSection)}
        </div>
      </div>
    </div>
  );
}
